/**
 * Sales offers: what we offer to sell before the buyer's confirmation (spec
 * docs/superpowers/specs/2026-10-14-sales-offers-before-contract-design.md, OF-1 to OF-8, approved by Ronald
 * on 14 October 2026; tables bsd-knowledge v0.126).
 *
 * Our mail offers a quantity and loading days without a price; the buyer names a price; the buyer's yes makes a
 * draft sales order, and the buyer's own confirmation fills and submits it. Since bsd-knowledge v0.211 an offer is
 * an ERPNext Quotation, and the actions are ERPNext's at the platform's pin (design
 * 2026-10-28-offer-actions-like-erpnext, Rick on the decision sheet OA-1 to OA-5): Submit, Make sales order,
 * Update price, Set as lost, Cancel and Amend. Per week (Monday to Sunday, this week and the next, with a
 * stepper), each offer shows its status, the chain from quotation to submitted order, its days, price and
 * history, and the actions its status allows. Submit waits until the planner has checked that every barrel can be
 * filled full from the haulier's forecast collections; once ordered, each loading day shows J.H. Willis's farms of that day from
 * Plan, the plan in operation (16 October 2026: the written haulier advice read the optimiser's runs and
 * said "No plan covers this day yet"), and beside it the D600 litres recorded that day.
 *
 * Milk trading and admin write (`can_write` from the API); the milk office and management read. The text to
 * copy is shown in a read-only field, so it can be selected by hand where the clipboard is not allowed.
 */
import { Badge, Button, Checkbox, Select, SelectItem, TextArea, TextField, type Tone, Table } from "@depicomnlbv/platform-ui";
import { useEffect, useRef, useState, type FormEvent } from "react";

import {
  useBuyers,
  useCreateOffer,
  useLinkOffer,
  useOfferAction,
  useOfferDays,
  useOfferPrice,
  useOffers,
  type Offer,
  type OfferAction,
  type OfferDay,
  type OfferDayBody,
  type OfferList,
  type OfferStatus,
  type QuotationStatus,
} from "../api";
import { useOperatingPlan } from "../api/milkFlow";
import { Pending } from "../components/Failure";
import { formatPrice, parsePrice } from "../salesForm";
import { addDays, formatLitres, formatShortDate, isCalendarDate, mondayOf } from "../time";
import { detailOf, focusFirstInvalid, ScreenRefused } from "./salesRefusal";

/** ERPNext's words for a quotation's status, on the badge. */
const QUOTATION: Record<QuotationStatus, { label: string; tone: Tone }> = {
  draft: { label: "Draft", tone: "neutral" },
  open: { label: "Open", tone: "medium" },
  ordered: { label: "Ordered", tone: "accent" },
  lost: { label: "Lost", tone: "low" },
  cancelled: { label: "Cancelled", tone: "low" },
};

/** The history's words: the planner's steps as ERPNext names them. */
const STEP: Record<OfferStatus, string> = {
  proposed: "Draft",
  offered: "Submitted",
  agreed: "Ordered",
  contracted: "Confirmed by the buyer",
  withdrawn: "Lost or cancelled",
};

/** Focus what ``find`` returns once it is on the page: a form that opens, a button that comes back when it closes,
 *  or a card that a refetch brings in. A control that unmounts under focus would leave it nowhere (WCAG 2.4.3). */
function focusSoon(find: () => Element | null | undefined, tries = 60) {
  const attempt = (left: number) => {
    const el = find();
    if (el instanceof HTMLElement) el.focus();
    else if (left > 0) window.setTimeout(() => attempt(left - 1), 50);
  };
  window.setTimeout(() => attempt(tries), 0);
}

/** The first control in ``box``, to move focus into a form when it opens. */
const firstField = (box: HTMLElement | null) =>
  box?.querySelector<HTMLElement>("input:not([type=hidden]), textarea, select, button");

/** A button in ``box`` whose name starts with ``label``: the trigger to give focus back to. */
const buttonNamed = (box: HTMLElement | null, label: string) =>
  Array.from(box?.querySelectorAll("button") ?? []).find((b) => (b.textContent ?? "").startsWith(label));

/** The id of an offer's title, which takes focus after an action changes or replaces the card. */
const titleId = (offerId: number) => `offer-${offerId}-title`;

/** The order is submitted (the buyer's confirmation is in): the offer's own actions end there. */
const confirmed = (o: Offer) => o.status === "contracted" || o.order?.status === "submitted";

const weekday = new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" });

/** `Mon 19 Oct 2026`. */
function day(date: string): string {
  return `${weekday.format(new Date(`${date}T00:00:00Z`))} ${formatShortDate(date)}`;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The text in a read-only field and a button that copies it; the result is said in a status. */
function CopyText({ label, text, description }: { label: string; text: string; description?: string }) {
  const [said, setSaid] = useState<string | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setSaid("Copied.");
    } catch {
      setSaid("Could not copy: select the text and copy it by hand.");
    }
  };
  return (
    <div className="milk-sales-offer__copy">
      <TextArea description={description} isReadOnly label={label} rows={Math.min(8, text.split("\n").length + 1)} value={text} />
      <div className="milk-sales__actions">
        <Button onPress={() => void copy()} variant="secondary">
          Copy<span className="visually-hidden">: {label}</span>
        </Button>
        <span aria-live="polite" className="milk-sales__detail">
          {said}
        </span>
      </div>
    </div>
  );
}

function FitState({ offer }: { offer: Offer }) {
  const { fit } = offer;
  if (fit.state === "pending") {
    return <p className="milk-sales__detail">Fit check pending: the planner checks these days within the hour.</p>;
  }
  if (fit.state === "fits") {
    return <p className="milk-sales__status">Fits: every barrel can be filled.</p>;
  }
  return (
    <div>
      <p className="milk-sales__error">Does not fit:</p>
      <ul className="milk-sales-table__lines">
        {fit.days.map((d) => (
          <li key={d.day}>{d.sentence}</li>
        ))}
      </ul>
    </div>
  );
}

type DayDraft = { key: string; date: string; loads: string };
let lastKey = 0;
const draftOf = (d?: { loading_date: string; loads: number }): DayDraft => ({
  key: `day-${++lastKey}`,
  date: d?.loading_date ?? "",
  loads: d ? String(d.loads) : "",
});

/** Day rows (date and loads), checked against a period. Errors per row key; null when the rows are good. */
function checkDays(drafts: DayDraft[], from: string, to: string): { errors: Record<string, string>; days: OfferDayBody[] } {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();
  const days: OfferDayBody[] = [];
  for (const d of drafts) {
    if (!isCalendarDate(d.date)) errors[`${d.key}.date`] = "The loading day is needed";
    else if (d.date < from || d.date > to) errors[`${d.key}.date`] = `A day from ${formatShortDate(from)} to ${formatShortDate(to)}`;
    else if (seen.has(d.date)) errors[`${d.key}.date`] = "This day is given twice";
    if (!/^\d+$/.test(d.loads.trim()) || Number(d.loads) < 1) errors[`${d.key}.loads`] = "A whole number of loads";
    if (isCalendarDate(d.date)) seen.add(d.date);
    days.push({ loading_date: d.date, loads: Number(d.loads) });
  }
  if (drafts.length === 0) errors.days = "An offer needs at least one loading day";
  return { errors, days };
}

function DayRows({
  drafts,
  errors,
  onChange,
}: {
  drafts: DayDraft[];
  errors: Record<string, string>;
  onChange: (drafts: DayDraft[]) => void;
}) {
  return (
    <>
      {drafts.map((d, i) => (
        <fieldset className="milk-sales-line" key={d.key}>
          <legend>Loading day {i + 1}</legend>
          <div className="milk-sales-line__fields">
            <TextField
              errorMessage={errors[`${d.key}.date`]}
              isInvalid={Boolean(errors[`${d.key}.date`])}
              label="Day"
              onChange={(date) => onChange(drafts.map((x) => (x.key === d.key ? { ...x, date } : x)))}
              type="date"
              value={d.date}
            />
            <TextField
              errorMessage={errors[`${d.key}.loads`]}
              inputMode="numeric"
              isInvalid={Boolean(errors[`${d.key}.loads`])}
              label="Loads"
              onChange={(loads) => onChange(drafts.map((x) => (x.key === d.key ? { ...x, loads } : x)))}
              value={d.loads}
            />
          </div>
          <div className="milk-sales__actions">
            <Button onPress={() => onChange(drafts.filter((x) => x.key !== d.key))} variant="subtle">
              Remove loading day {i + 1}
            </Button>
          </div>
        </fieldset>
      ))}
      {errors.days ? <p className="milk-sales__error">{errors.days}</p> : null}
      <div className="milk-sales__actions">
        <Button onPress={() => onChange([...drafts, draftOf()])} variant="secondary">
          Add a loading day
        </Button>
      </div>
    </>
  );
}

function EditDays({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  const save = useOfferDays();
  const [drafts, setDrafts] = useState<DayDraft[]>(() => offer.days.map(draftOf));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refused, setRefused] = useState<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  useEffect(() => focusSoon(() => firstField(form.current)), []);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (save.isPending) return;
    setRefused(null);
    const { errors: found, days } = checkDays(drafts, offer.period_from, offer.period_to);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstInvalid(form.current);
      return;
    }
    save.mutate({ offerId: offer.offer_id, days }, { onSuccess: onClose, onError: (e) => setRefused(detailOf(e)) });
  };
  return (
    <form aria-label={`New days for offer ${offer.offer_id}`} className="milk-sales-form" noValidate onSubmit={submit} ref={form}>
      <p className="milk-sales__detail">The planner checks the new days again within the hour.</p>
      <DayRows drafts={drafts} errors={errors} onChange={setDrafts} />
      <div className="milk-sales__actions">
        <Button isPending={save.isPending} pendingLabel="Saving" type="submit">
          Save days<span className="visually-hidden">: offer {offer.offer_id}</span>
        </Button>
        <Button onPress={onClose} variant="subtle">
          Keep the days
        </Button>
      </div>
      {refused ? (
        <p className="milk-sales__error" role="alert">
          {refused}
        </p>
      ) : null}
    </form>
  );
}

/** ERPNext's Set as Lost dialog, in place: the reasons (one at least, from the list), the competitors it went to
 *  (one not on the list can be added) and a detailed reason. */
function SetAsLost({
  offer,
  reasons,
  competitors,
  mailRef,
  onDone,
  onClose,
}: {
  offer: Offer;
  reasons: string[];
  competitors: string[];
  mailRef: string | null;
  onDone: () => void;
  onClose: () => void;
}) {
  const action = useOfferAction();
  const [chosen, setChosen] = useState<string[]>([]);
  const [rivals, setRivals] = useState<string[]>([]);
  const [other, setOther] = useState("");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const id = offer.offer_id;
  const errorId = `lost-error-${id}`;
  useEffect(() => focusSoon(() => firstField(form.current)), []);
  const toggle = (list: string[], name: string, on: boolean) => (on ? [...list, name] : list.filter((x) => x !== name));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (action.isPending) return;
    setRefused(null);
    if (chosen.length === 0) {
      setError("Choose at least one reason");
      focusFirstInvalid(form.current);
      return;
    }
    setError(null);
    const named = other.trim() ? [...rivals, other.trim()] : rivals;
    action.mutate(
      {
        offerId: id,
        action: {
          action: "set-as-lost",
          body: { lost_reasons: chosen, competitors: named, detailed_reason: detail.trim() || null, mail_ref: mailRef },
        },
      },
      { onSuccess: onDone, onError: (e) => setRefused(detailOf(e)) },
    );
  };

  return (
    <form aria-label={`Set offer ${id} as lost`} className="milk-sales-form" noValidate onSubmit={submit} ref={form}>
      <fieldset aria-describedby={error ? errorId : undefined} className="milk-sales-line">
        <legend>Lost reasons</legend>
        {reasons.map((r) => (
          <Checkbox
            aria-describedby={error ? errorId : undefined}
            isInvalid={Boolean(error)}
            isSelected={chosen.includes(r)}
            key={r}
            onChange={(on) => setChosen(toggle(chosen, r, on))}
          >
            {r}
          </Checkbox>
        ))}
        {error ? (
          <p className="milk-sales__error" id={errorId} role="alert">
            {error}
          </p>
        ) : null}
      </fieldset>
      <fieldset className="milk-sales-line">
        <legend>Competitors (optional)</legend>
        {competitors.map((c) => (
          <Checkbox isSelected={rivals.includes(c)} key={c} onChange={(on) => setRivals(toggle(rivals, c, on))}>
            {c}
          </Checkbox>
        ))}
        <TextField
          description="A buyer not on the list; it is added to it."
          label="Another competitor"
          onChange={setOther}
          value={other}
        />
      </fieldset>
      <TextArea label="Detailed reason (optional)" onChange={setDetail} rows={2} value={detail} />
      <div className="milk-sales__actions">
        <Button isPending={action.isPending} pendingLabel="Setting as lost" type="submit">
          Set offer {id} as lost
        </Button>
        <Button onPress={onClose} variant="subtle">
          Keep it
        </Button>
      </div>
      {refused ? (
        <p className="milk-sales__error" role="alert">
          {refused}
        </p>
      ) : null}
    </form>
  );
}

/** The actions the offer's status allows, for trading and admin: ERPNext's Quotation actions. */
function Actions({
  offer,
  list,
  editFirst = false,
  onAmended,
}: {
  offer: Offer;
  list: OfferList;
  /** Open the days for editing at once: the new draft of an Amend (design point 4). */
  editFirst?: boolean;
  onAmended?: (offerId: number) => void;
}) {
  const action = useOfferAction();
  const price = useOfferPrice();
  const link = useLinkOffer();
  const box = useRef<HTMLDivElement | null>(null);
  const cancelBox = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState(editFirst);
  const [showMail, setShowMail] = useState(false);
  const [open, setOpen] = useState<"cancel" | "lost" | null>(null);
  const [mailRef, setMailRef] = useState("");
  const [note, setNote] = useState("");
  const [priceText, setPriceText] = useState("");
  const [basis, setBasis] = useState(offer.price_basis ?? "");
  const [priceError, setPriceError] = useState<string | null>(null);
  const [contract, setContract] = useState<number | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const id = offer.offer_id;
  const q = offer.quotation_status;
  const onError = (e: unknown) => setRefused(detailOf(e));
  const ref = mailRef.trim() || null;
  const run = (a: OfferAction, onSuccess?: (written: { offer_id: number }) => void) => {
    setRefused(null);
    setDone(null);
    action.mutate({ offerId: id, action: a }, { onError, onSuccess });
  };
  /** Close a form and give focus back to the button that opened it. */
  const close = (label: string, shut: () => void) => {
    shut();
    focusSoon(() => buttonNamed(box.current, label));
  };
  /** After an action that changes the card, its title holds focus while the actions under it change. */
  const toTitle = () => focusSoon(() => document.getElementById(titleId(id)));
  useEffect(() => {
    if (open === "cancel") focusSoon(() => firstField(cancelBox.current));
  }, [open]);
  const named = (label: string) => (
    <>
      {label}
      <span className="visually-hidden">: offer {id}</span>
    </>
  );

  const mailField = (
    <TextField
      description="Optional: the Graph message id or a link to the mail this action rests on."
      label="Mail reference"
      onChange={setMailRef}
      value={mailRef}
    />
  );

  /** ERPNext's Create > Sales Order at the buyer's price: the price typed here if there is one, else the one
   *  recorded. Without either it asks for the price instead of making an order without one. */
  const makeOrder = () => {
    setRefused(null);
    const typed = priceText.trim();
    if (typed) {
      const perLitre = parsePrice(typed);
      if (perLitre === null || !(perLitre > 0 && perLitre < 2)) {
        setPriceError("A price in pence per litre with two decimals at most, such as 40.00");
        focusFirstInvalid(box.current);
        return;
      }
      setPriceError(null);
      run({ action: "make-sales-order",
        body: { price_gbp_per_litre: perLitre, price_basis: basis.trim() || null, mail_ref: ref } }, toTitle);
      return;
    }
    if (offer.price_gbp_per_litre === null) {
      setPriceError("The buyer's price is needed to make the sales order");
      focusFirstInvalid(box.current);
      return;
    }
    setPriceError(null);
    run({ action: "make-sales-order", body: { mail_ref: ref } }, toTitle);
  };

  const updatePrice = () => {
    setRefused(null);
    const perLitre = parsePrice(priceText);
    if (perLitre === null || !(perLitre > 0 && perLitre < 2)) {
      setPriceError("A price in pence per litre with two decimals at most, such as 40.00");
      return;
    }
    setPriceError(null);
    price.mutate(
      { offerId: id, body: { price_gbp_per_litre: perLitre, price_basis: basis.trim() || null, mail_ref: ref } },
      { onSuccess: () => setPriceText(""), onError },
    );
  };

  // Set as lost on a submitted offer, until the buyer's confirmation submits the order (ERPNext's rule); it needs
  // the quotations of v0.211.
  const canLose = list.sales_documents && (q === "open" || (q === "ordered" && !confirmed(offer)));
  const canCancel = q === "draft" || q === "open" || (q === "ordered" && !confirmed(offer));

  return (
    <div className="milk-sales-offer__actions" ref={box}>
      {q === "draft" ? (
        <>
          {editing ? (
            <EditDays offer={offer} onClose={() => close("Edit days", () => setEditing(false))} />
          ) : (
            <div className="milk-sales__actions">
              <Button onPress={() => setEditing(true)} variant="secondary">
                {named("Edit days")}
              </Button>
              <Button onPress={() => setShowMail(!showMail)} variant="secondary">
                {named(showMail ? "Hide mail text" : "Copy mail text")}
              </Button>
            </div>
          )}
          {showMail ? <CopyText label={`Mail text of offer ${id}`} text={offer.mail_text} /> : null}
          {mailField}
          <div className="milk-sales__actions">
            <Button
              isDisabled={offer.fit.state !== "fits"}
              isPending={action.isPending}
              onPress={() => run({ action: "submit", body: { mail_ref: ref } }, toTitle)}
              pendingLabel="Submitting"
            >
              {named("Submit")}
            </Button>
          </div>
          {offer.fit.state !== "fits" ? (
            <p className="milk-sales__detail">Submit once the planner says every barrel fits.</p>
          ) : null}
        </>
      ) : null}

      {q === "open" ? (
        <>
          <div className="milk-sales-form__pair">
            <TextField
              description="In pence per litre, such as 40.00."
              errorMessage={priceError ?? undefined}
              inputMode="decimal"
              isInvalid={Boolean(priceError)}
              label="The buyer's price (ppl)"
              onChange={setPriceText}
              value={priceText}
            />
            <TextField
              description="As written, such as “ex Gresford”."
              label="Price basis"
              onChange={setBasis}
              value={basis}
            />
          </div>
          {mailField}
          <div className="milk-sales__actions">
            <Button isPending={price.isPending} onPress={updatePrice} pendingLabel="Updating" variant="secondary">
              {named("Update price")}
            </Button>
            <Button isPending={action.isPending} onPress={makeOrder} pendingLabel="Making the sales order">
              {named("Make sales order")}
            </Button>
          </div>
          <p className="milk-sales__detail">
            {offer.price_gbp_per_litre === null
              ? "Make sales order takes the price typed above; none is recorded yet."
              : "Make sales order takes the price typed above, or the recorded one when the field is empty."}
          </p>
        </>
      ) : null}

      {q === "ordered" && !confirmed(offer) ? (
        offer.linkable_contracts.length === 0 ? (
          <p className="milk-sales__detail">
            The draft sales order waits for {offer.buyer}'s confirmation, which fills and submits it when it is
            recorded under Contracts.
          </p>
        ) : (
          <div className="milk-sales__actions">
            <Select
              label="The buyer's contract"
              onSelectionChange={(key) => setContract(key === null ? null : Number(key))}
              placeholder="Choose the contract"
              selectedKey={contract === null ? null : String(contract)}
            >
              {offer.linkable_contracts.map((c) => (
                <SelectItem id={String(c.contract_id)} key={c.contract_id} textValue={c.contract_number}>
                  {c.contract_number} ({formatShortDate(c.period_from)} to {formatShortDate(c.period_to)})
                </SelectItem>
              ))}
            </Select>
            <Button
              isDisabled={contract === null}
              isPending={link.isPending}
              onPress={() => {
                setRefused(null);
                if (contract !== null) link.mutate({ offerId: id, contractId: contract }, { onError });
              }}
              pendingLabel="Linking"
            >
              {named("Link contract")}
            </Button>
          </div>
        )
      ) : null}

      {open === "lost" ? (
        <SetAsLost
          competitors={list.competitors}
          mailRef={ref}
          offer={offer}
          onClose={() => close("Set as lost", () => setOpen(null))}
          onDone={() => {
            setOpen(null);
            toTitle();
          }}
          reasons={list.lost_reasons}
        />
      ) : null}

      {open === "cancel" ? (
        <div className="milk-sales-form__reject" ref={cancelBox}>
          <TextArea
            description="Optional: why we withdraw it, kept with its history."
            label={`Why is offer ${id} cancelled?`}
            onChange={setNote}
            rows={2}
            value={note}
          />
          <div className="milk-sales__actions">
            <Button
              isPending={action.isPending}
              onPress={() =>
                run({ action: "cancel", body: { mail_ref: ref, note: note.trim() || null } }, () => {
                  setOpen(null);
                  toTitle();
                })
              }
              pendingLabel="Cancelling"
            >
              Cancel offer {id}
            </Button>
            <Button onPress={() => close("Cancel", () => setOpen(null))} variant="subtle">
              Keep it
            </Button>
          </div>
        </div>
      ) : null}

      {open === null && (canLose || canCancel) ? (
        <div className="milk-sales__actions">
          {canLose ? (
            <Button onPress={() => setOpen("lost")} variant="secondary">
              {named("Set as lost")}
            </Button>
          ) : null}
          {canCancel ? (
            <Button onPress={() => setOpen("cancel")} variant="subtle">
              {named("Cancel")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {q === "cancelled" && list.sales_documents && !offer.amended_as ? (
        <div className="milk-sales__actions">
          <Button
            isPending={action.isPending}
            onPress={() =>
              run({ action: "amend" }, (w) => {
                setDone(`Amended: offer ${w.offer_id} is its new draft, open for its days below.`);
                onAmended?.(w.offer_id);
              })
            }
            pendingLabel="Amending"
            variant="secondary"
          >
            {named("Amend")}
          </Button>
        </div>
      ) : null}

      <div aria-live="polite">
        {done ? (
          <p className="milk-sales__status" role="status">
            {done}
          </p>
        ) : null}
      </div>
      {refused ? (
        <p className="milk-sales__error" role="alert">
          {refused}
        </p>
      ) : null}
    </div>
  );
}

/** The chain from the quotation to the submitted order, as ERPNext's documents: quotation, sales order (a draft from
 *  the buyer's yes), the buyer's confirmation, and the order submitted with it. A step not reached says so. */
function Chain({ offer }: { offer: Offer }) {
  const q = offer.quotation_status;
  const order = offer.order;
  const contract = order?.contract_number ?? offer.contract_number;
  const quotation = offer.quotation_name ? `Quotation ${offer.quotation_name}` : `Offer ${offer.offer_id}`;
  const steps: { text: string; reached: boolean }[] = [
    {
      text: `${quotation}: ${QUOTATION[q].label}${offer.amended_from ? `, amends ${offer.amended_from}` : ""}${
        offer.amended_as ? `, amended as ${offer.amended_as}` : ""
      }`,
      reached: true,
    },
    {
      text: order
        ? `Sales order ${order.name}: ${order.status === "draft" ? `draft, waiting for ${offer.buyer}'s confirmation` : "submitted"}`
        : confirmed(offer)
          ? "Sales order: submitted"
          : q === "ordered"
            ? `Sales order: made when ${offer.buyer}'s confirmation is recorded`
            : "Sales order: not made yet",
      reached: Boolean(order) || confirmed(offer),
    },
    {
      text: contract
        ? `${offer.buyer}'s contract ${contract}${order ? (order.checked ? ", checked" : ", not checked yet") : ""}`
        : `${offer.buyer}'s confirmation: not in yet`,
      reached: Boolean(contract),
    },
  ];
  return (
    <div>
      <ol aria-label={`The documents of offer ${offer.offer_id}`} className="milk-sales-offer__chain">
        {steps.map((s, i) => (
          <li className={s.reached ? undefined : "milk-sales-offer__chain--pending"} key={i}>
            {s.text}
          </li>
        ))}
      </ol>
      {q === "lost" ? (
        <p className="milk-sales__detail">
          Lost{offer.lost_reasons.length ? `: ${offer.lost_reasons.join(", ")}` : ""}.
          {offer.competitors.length ? ` To ${offer.competitors.join(", ")}.` : ""}
          {offer.detailed_reason ? ` ${offer.detailed_reason}` : ""}
        </p>
      ) : null}
      {q === "cancelled" ? <p className="milk-sales__detail">Cancelled by us.</p> : null}
    </div>
  );
}

function soldText(d: OfferDay): string {
  if (d.sold_litres.length === 0) return "None recorded";
  return d.sold_litres.map((l) => (l === null ? "no D600 yet" : formatLitres(l))).join(", ");
}

/** One loading day's J.H. Willis farms from the plan in operation (Plan, 16 October 2026), in place of the
 *  written haulier advice that read the optimiser's runs: the farms of that day fill the barrels first (MF-16). */
function LoadingDayRow({ day: d, today }: { day: string; today: string | null }) {
  const plan = useOperatingPlan(d, 1, today !== null && d >= today);
  const willis = plan.data?.days_plan[0]?.vehicles.find((v) => v.vehicle === "willis");
  return (
    <tr>
      <th className="tabular" scope="row">
        {day(d)}
      </th>
      <td>{willis ? willis.farms.map((f) => f.farm_name).join(", ") : "—"}</td>
      <td className="tabular">{willis ? formatLitres(willis.to_barrels_litres) : "—"}</td>
    </tr>
  );
}

function LoadingDayFarms({ days }: { days: string[] }) {
  const plan = useOperatingPlan(undefined, 1);
  const today = plan.data?.today ?? null;
  const coming = days.filter((d) => today !== null && d >= today);
  if (coming.length === 0) return null;
  return (
    <Table caption="J.H. Willis's farms on the loading days, from Plan" className="milk-sales-table">
      <thead>
        <tr>
          <th scope="col">Day</th>
          <th scope="col">Farms</th>
          <th scope="col">To the barrels</th>
        </tr>
      </thead>
      <tbody>
        {coming.map((d) => (
          <LoadingDayRow day={d} key={d} today={today} />
        ))}
      </tbody>
    </Table>
  );
}

function OfferCard({
  offer,
  list,
  editFirst,
  onAmended,
}: {
  offer: Offer;
  list: OfferList;
  editFirst: boolean;
  onAmended: (offerId: number) => void;
}) {
  const headingId = titleId(offer.offer_id);
  const status = QUOTATION[offer.quotation_status];
  const advised = offer.status === "agreed" || offer.status === "contracted";
  const first = offer.days.map((d) => d.loading_date).sort()[0];
  // ERPNext would expire it past its valid-till date; here the screen says so and a person decides (OA-4).
  const unanswered = offer.quotation_status === "open" && first !== undefined && first < list.today;
  return (
    <article aria-labelledby={headingId} className="milk-sales-offer">
      <h5 className="milk-sales-offer__title" id={headingId} tabIndex={-1}>
        Offer {offer.offer_id} to {offer.buyer}: {plural(offer.loads, "load")}
      </h5>
      <div className="milk-sales__actions">
        <Badge tone={confirmed(offer) ? "done" : status.tone}>{status.label}</Badge>
      </div>
      <Chain offer={offer} />
      {unanswered ? (
        <p className="milk-sales__error">First loading day passed without an answer: set it as lost or cancel it.</p>
      ) : null}
      <p className="milk-sales__detail">
        {formatShortDate(offer.period_from)} to {formatShortDate(offer.period_to)}, loads of{" "}
        {formatLitres(offer.litres_per_load)}
        {offer.location ? ` at ${offer.location}` : ""}.{" "}
        {offer.price_gbp_per_litre !== null
          ? `Price ${formatPrice(offer.price_gbp_per_litre)}${offer.price_basis ? `, ${offer.price_basis}` : ""}.`
          : "No price from the buyer yet."}
      </p>
      <Table caption={`Loading days of offer ${offer.offer_id}`} className="milk-sales-table">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Loads</th>
              <th scope="col">Litres agreed</th>
              {advised ? <th scope="col">Sold (D600)</th> : null}
            </tr>
          </thead>
          <tbody>
            {offer.days.map((d) => (
              <tr key={d.loading_date}>
                <th className="tabular" scope="row">
                  {day(d.loading_date)}
                </th>
                <td className="tabular">{d.loads}</td>
                <td className="tabular">{formatLitres(d.litres)}</td>
                {advised ? <td className="tabular">{soldText(d)}</td> : null}
              </tr>
            ))}
          </tbody>
        </Table>
      {offer.quotation_status === "draft" ? <FitState offer={offer} /> : null}
      {advised ? <LoadingDayFarms days={offer.days.map((d) => d.loading_date)} /> : null}
      {list.can_write ? <Actions editFirst={editFirst} list={list} offer={offer} onAmended={onAmended} /> : null}
      <details className="milk-sales-held__text">
        <summary>History of offer {offer.offer_id}</summary>
        <ul className="milk-sales-table__lines">
          {offer.steps.map((s, i) => (
            <li key={i}>
              {STEP[s.status as OfferStatus] ?? s.status} on {formatShortDate(s.stepped_at.slice(0, 10))} by{" "}
              {s.stepped_by ?? "the planner"}
              {s.mail_ref ? `, mail ${s.mail_ref}` : ""}
              {s.note ? `: ${s.note}` : ""}
            </li>
          ))}
        </ul>
      </details>
    </article>
  );
}

function NewOfferForm({ firstMonday }: { firstMonday: string }) {
  const buyers = useBuyers();
  const create = useCreateOffer();
  const [buyer, setBuyer] = useState<number | null>(null);
  const [week, setWeek] = useState(addDays(firstMonday, 7));
  const [perLoad, setPerLoad] = useState("28000");
  const [drafts, setDrafts] = useState<DayDraft[]>(() => [draftOf()]);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const only = buyers.data?.buyers.length === 1 ? buyers.data.buyers[0]!.buyer_id : null;
  const chosen = buyer ?? only;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (create.isPending) return;
    setDone(null);
    setRefused(null);
    const found: Record<string, string> = {};
    if (chosen === null) found.buyer = "Choose the buyer";
    const monday = isCalendarDate(week) ? mondayOf(week) : null;
    if (monday === null) found.week = "A day in the week the offer is for";
    if (!/^\d+$/.test(perLoad.trim()) || Number(perLoad) < 1) found.perLoad = "Whole litres, such as 28000";
    const checked = monday ? checkDays(drafts, monday, addDays(monday, 6)) : { errors: {}, days: [] };
    Object.assign(found, checked.errors);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstInvalid(form.current);
      return;
    }
    create.mutate(
      {
        buyer_id: chosen!,
        period_from: monday!,
        period_to: addDays(monday!, 6),
        litres_per_load: Number(perLoad),
        days: checked.days,
        notes: notes.trim() || null,
      },
      {
        onSuccess: (o) => {
          setDone(`Proposed: offer ${o.offer_id}. The planner checks its days within the hour.`);
          setDrafts([draftOf()]);
          setNotes("");
        },
        onError: (e) => setRefused(detailOf(e)),
      },
    );
  };

  return (
    <form aria-labelledby="new-offer-heading" className="milk-sales-form" noValidate onSubmit={submit} ref={form}>
      <h4 id="new-offer-heading">New offer</h4>
      {buyers.isError ? (
        <p className="milk-sales__error">The buyers could not be read: {detailOf(buyers.error)}</p>
      ) : (
        <Select
          errorMessage={errors.buyer}
          isInvalid={Boolean(errors.buyer)}
          label="Buyer"
          onSelectionChange={(key) => setBuyer(key === null ? null : Number(key))}
          placeholder={buyers.isPending ? "Reading the buyers" : "Choose the buyer"}
          selectedKey={chosen === null ? null : String(chosen)}
        >
          {(buyers.data?.buyers ?? []).map((b) => (
            <SelectItem id={String(b.buyer_id)} key={b.buyer_id} textValue={b.name}>
              {b.name}
            </SelectItem>
          ))}
        </Select>
      )}
      <div className="milk-sales-form__pair">
        <TextField
          description="Any day of it: the offer runs Monday to Sunday."
          errorMessage={errors.week}
          isInvalid={Boolean(errors.week)}
          label="Week"
          onChange={setWeek}
          type="date"
          value={week}
        />
        <TextField
          errorMessage={errors.perLoad}
          inputMode="numeric"
          isInvalid={Boolean(errors.perLoad)}
          label="Litres per load"
          onChange={setPerLoad}
          value={perLoad}
        />
      </div>
      <DayRows drafts={drafts} errors={errors} onChange={setDrafts} />
      <TextArea label="Notes" onChange={setNotes} rows={2} value={notes} />
      <div className="milk-sales__actions">
        <Button isPending={create.isPending} pendingLabel="Proposing" type="submit">
          Propose offer
        </Button>
      </div>
      <div aria-live="polite">
        {done ? (
          <p className="milk-sales__status" role="status">
            {done}
          </p>
        ) : null}
      </div>
      {refused ? (
        <p className="milk-sales__error" role="alert">
          {refused}
        </p>
      ) : null}
    </form>
  );
}

/** The offers of this week and the next, Monday to Sunday, with a stepper. */
export function Offers({ buyer = null }: { buyer?: string | null }) {
  const [monday, setMonday] = useState<string | null>(null);
  // The new draft of an Amend opens for editing, and focus goes into it (design point 4).
  const [amended, setAmended] = useState<number | null>(null);
  const offers = useOffers(monday);
  const shown = monday ?? offers.data?.from ?? null;

  let body;
  if (offers.isPending) body = <Pending>Reading the offers</Pending>;
  else if (offers.isError) body = <ScreenRefused error={offers.error} part="offers" />;
  else {
    const first = mondayOf(offers.data.from);
    const weeks = [first, addDays(first, 7)];
    body = (
      <>
        <div className="milk-sales__actions">
          <Button onPress={() => setMonday(addDays(first, -7))} variant="subtle">
            Week before
          </Button>
          <Button onPress={() => setMonday(addDays(first, 7))} variant="subtle">
            Week after
          </Button>
        </div>
        {weeks.map((w) => {
          const end = addDays(w, 6);
          const these = offers.data.offers.filter(
            (o) =>
              (buyer === null || o.buyer === buyer) &&
              (w === first ? o.period_from <= end : o.period_from >= w && o.period_from <= end),
          );
          return (
            <section aria-label={`Week of ${day(w)}`} className="milk-sales-offer__week" key={w}>
              <h4>Week of {day(w)}</h4>
              {these.length === 0 ? (
                <p className="empty">No offer for this week.</p>
              ) : (
                these.map((o) => (
                  <OfferCard
                    editFirst={o.offer_id === amended}
                    key={o.offer_id}
                    list={offers.data}
                    offer={o}
                    onAmended={setAmended}
                  />
                ))
              )}
            </section>
          );
        })}
        {offers.data.can_write ? <NewOfferForm firstMonday={first} /> : null}
      </>
    );
  }

  return (
    <section aria-labelledby="offers-heading" className="panel">
      <h3 id="offers-heading">Offers</h3>
      <p className="milk-sales__detail">
        What we offer to sell, before the buyer's confirmation{shown ? `, from the week of ${day(mondayOf(shown))}` : ""}.
      </p>
      {body}
    </section>
  );
}
